import {
	Box,
	Typography,
	FormControl,
	FormLabel,
	Chip,
	Stack,
	Paper,
	Grid,
	IconButton,
} from '@mui/material';

import { ChangeEvent, FC, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import { ReactComponent as InfoIcon } from 'common/assets/images/info-variant.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import ModalEle from 'common/components/Modal/ModalEle';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { fetchHighlights, saveChatRecord } from '../../api';
import { IDuplicateObj, IHighlight } from '../../api/type-def';

import style from './SaveChatModal.module.scss';

interface ISaveChatModal {
	handleClose: () => void;
	open: boolean;
	sessionId: string;
}
const SaveChatModal: FC<ISaveChatModal> = ({
	handleClose,
	open,
	sessionId,
}) => {
	const dispatch = useDispatch();

	const [recordName, setRecordName] = useState<string>('');
	const [allSelected, setAllSelected] = useState<boolean>(false);
	const [chatHighlights, setChatHighlights] = useState<IHighlight[] | []>([]); // Add type
	const [selectedHighlights, setSelectedHighlights] = useState<
		IHighlight[] | []
	>([]);
	const [highlightLoading, setHighlightLoading] = useState<boolean>(false);
	const [saveLoading, setSaveLoading] = useState<boolean>(false);
	const [duplicateErrorObj, setDuplicateErrorObj] = useState<IDuplicateObj>({
		errorStatus: false,
		errorMsg: '',
	});

	const { OK } = HTTP_STATUS_CODES;

	const fetchChatHighlights = async () => {
		setHighlightLoading(true);
		const payload = { session_id: sessionId };
		const resp = await fetchHighlights(payload);
		if (resp.responseBody && resp.statusCode === OK) {
			setChatHighlights(resp?.responseBody);
			setHighlightLoading(false);
		}
	};
	useEffect(() => {
		if (chatHighlights.length === 0) {
			fetchChatHighlights();
		}
	}, []);

	const handleSave = async () => {
		setSaveLoading(true);
		const payload = {
			record_name: recordName,
			session_id: sessionId,
			thread_highlights: allSelected ? chatHighlights : selectedHighlights,
		};
		const resp = await saveChatRecord(payload);
		if (resp.responseBody && resp.statusCode === OK) {
			setSaveLoading(false);
			dispatch(
				setToastMessage({
					message: 'Record saved successfully',
					severity: 'success',
					isToastOpen: true,
				}),
			);
			handleClose();
		} else if (resp?.errorBody || resp?.error) {
			const { errorBody } = resp;
			const responseError: any = resp.error;
			const errorMessage =
				(errorBody?.error as string) || (responseError?.message as string);
			if (errorMessage?.includes('duplicate')) {
				setDuplicateErrorObj({
					errorStatus: true,
					errorMsg: errorMessage,
				});
			}
			setSaveLoading(false);
		}
	};

	const handleHighlightClick = (highlightObj: IHighlight) => {
		const { highlight } = highlightObj;

		if (highlight === 'All') {
			setSelectedHighlights([highlightObj]);
			setAllSelected(true);
		} else {
			setSelectedHighlights((prev) => {
				const newHighlights = prev.some((h) => h.highlight === highlight)
					? prev.filter((h) => h.highlight !== highlight)
					: [...prev, highlightObj];

				// Remove 'All' if any other highlight is selected
				return newHighlights.filter((h) => h.highlight !== 'All');
			});
			setAllSelected(false);
		}
	};

	const { padding20 } = style;

	return (
		<ModalEle
			open={open}
			handleClose={handleClose}
			restrictDefaultCloseAction
			sxBoxProps={{
				width: '40%',
				margin: 'auto',
				padding: padding20,
			}}
		>
			<Box>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography
						variant="h6"
						component="h2"
						className={style['save-chat-title']}
					>
						Save Chat as a Record
					</Typography>
					<IconButton
						onClick={handleClose}
						disableRipple
						className={style['model-close-button']}
					>
						<CloseIcon
							fontSize="medium"
							className={style['closemodal-style']}
						/>
					</IconButton>{' '}
				</Box>{' '}
				<Typography sx={{ mt: 2, mb: 2 }}>
					Store your session as a documented record for future reference or
					review
				</Typography>
				<Paper className={style['highlight-paper']}>
					<Grid container className={style['highlight-container']}>
						<Grid
							item
							xs={1}
							alignItems="center"
							className={style['info-icon-container']}
						>
							<InfoIcon className={style['save-chat-info-icon']} />
						</Grid>
						<Grid item xs={11}>
							<Typography
								variant="body2"
								className={style['highlight-warning-text']}
							>
								Highlights help you quickly find and review important
								information without scrolling through the entire chat history.
							</Typography>
						</Grid>
					</Grid>
				</Paper>
				<FormControl component="fieldset" sx={{ mt: 2, mb: 2 }}>
					<FormLabel component="legend" className={style['select-ai-text']}>
						Select AI Thread Highlights
					</FormLabel>
					{highlightLoading ? (
						<Loader show={highlightLoading} className={style['loader-style']} />
					) : (
						<Box display="flex" flexWrap="wrap" gap={1} mt={2}>
							<Chip
								key="All"
								label="All"
								data-testid="chip-all"
								onClick={() =>
									handleHighlightClick({ highlight: 'All', message_id: 'all' })
								}
								color={allSelected ? 'primary' : 'default'}
							/>
							{chatHighlights?.map((highlightObj: IHighlight) => (
								<Chip
									key={highlightObj.message_id}
									data-testid={`chip-${highlightObj.message_id}`}
									label={toSentenceCase(highlightObj.highlight)}
									onClick={() => handleHighlightClick(highlightObj)}
									color={
										selectedHighlights?.some(
											(h) => h.message_id === highlightObj.message_id,
										)
											? 'primary'
											: 'default'
									}
								/>
							))}
						</Box>
					)}
				</FormControl>
				<TextFieldWithLabel
					label={toSentenceCase('Record Name')}
					inputProps={{
						'data-testid': 'record-name',
					}}
					error={duplicateErrorObj.errorStatus}
					required
					value={recordName}
					onChange={(event: ChangeEvent<HTMLInputElement>) => {
						setRecordName(event.target.value);
						setDuplicateErrorObj({
							errorStatus: false,
							errorMsg: '',
						});
					}}
					parentClass={style['word-txt-position']}
					helperText={
						duplicateErrorObj.errorStatus ? duplicateErrorObj.errorMsg : ''
					}
					showLabel
					className={style['text-field-style']}
					positionClass={style['word-limit-position']}
				/>
				<Stack className={style['btn-group']} direction="row" spacing={2}>
					<ButtonComp
						clickHandler={handleClose}
						btnType="secondary"
						data-testid="record-cancel"
					>
						Cancel
					</ButtonComp>
					<ButtonComp
						clickHandler={handleSave}
						btnType="primary"
						disabled={
							!recordName ||
							selectedHighlights.length === 0 ||
							duplicateErrorObj.errorStatus
						}
						data-testid="record-save"
						loading={saveLoading}
					>
						Save
					</ButtonComp>
				</Stack>
			</Box>
		</ModalEle>
	);
};

export default SaveChatModal;
